import * as React from 'react';

import {getTabbable} from './helpers/tabbable';

// Keeps Tab and Shift+Tab inside `children`: from the last element the Tab key
// can reach, Tab goes to the first, and Shift+Tab from the first goes to the
// last. With nothing to reach, focus stays where it is. Moving focus in when a
// dialog opens, and out when it closes, is `useDialogIsolation`'s job.
//
// The listener is a DOM one, not a React `onKeyDown`: a dialog opened from
// inside this one is a React child of it, and React would bubble that
// dialog's keys up through a portal to here.
const FocusTrap = ({
  active = true,
  children,
}: {
  active?: boolean;
  children: React.ReactNode;
}) => {
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const trap = ref.current;
    if (!active || !trap) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== 'Tab' ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        !(event.target instanceof Node)
      ) {
        return;
      }
      const from = event.target;
      const tabbable = getTabbable(trap);
      const ahead = event.shiftKey
        ? Node.DOCUMENT_POSITION_PRECEDING
        : Node.DOCUMENT_POSITION_FOLLOWING;
      if (
        tabbable.some(
          element =>
            element !== from && from.compareDocumentPosition(element) & ahead,
        )
      ) {
        // The browser's own move stays inside the trap.
        return;
      }
      event.preventDefault();
      (event.shiftKey ? tabbable[tabbable.length - 1] : tabbable[0])?.focus();
    };
    trap.addEventListener('keydown', onKeyDown);
    return () => {
      trap.removeEventListener('keydown', onKeyDown);
    };
  }, [active]);

  return <div ref={ref}>{children}</div>;
};

export default FocusTrap;
